import {
  type CreatCustomerDTO,
  type Currency,
  type CustomerDTO,
  type DbLogFields,
  type DiscountDTO,
  type ProductLightDTO,
  type ServiceDTO
} from './grand-connect'
import { type CartItem } from './cart'
import type { IngridCreateSessionResponse } from './ingrid'
import type { Option } from './general'
import type { NexiCreatePaymentResponse, NexiRetrievePaymentResponse, NexiUpdateOrderRequest } from './nexi'
import { type SveaOrder, type SveaUpdateOrderModel } from './svea'
import { type KlarnaOrder, type KlarnaUpdateOrderRequest } from './klarna'
import { type PostNordAddress, type PostNordCheckoutDeliveryOptionSelected } from './postnord'
import type { PaymentProviderName, ShippingProviderName } from './storefront'

export enum OrderStatus {
  CANCELLED = 'CANCELLED',
  CREATED = 'CREATED',
  DELIVERED = 'DELIVERED',
  DRAFT = 'DRAFT',
  PAID = 'PAID',
  PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED'
}

/** Data could be extended or changed without changing the JAVA BE */
export interface OrderShippingAdditionalDataDTO {
  postnordDetails?: {
    isPickupFromStore?: boolean
    recipientAddress?: PostNordAddress
    deliveryOption?: PostNordCheckoutDeliveryOptionSelected
    bookingDetails?: {
      bookingId: string
      trackingUrl?: string
      trackingNumber?: string
      labelUrl?: string
    }
  }
}

export interface OrderShippingDTO {
  /** Human-readable name of the carrier, e.g. "DHL", "PostNord" */
  carrierName?: string

  /** Service level like "Standard", "Express", "MyPack", etc. */
  carrierService?: string

  /** Unique code used for tracking the shipment */
  trackingNumber?: string

  /** Full URL to track the shipment online */
  trackingUrl?: string

  /** Optional link to the shipping label in PDF format (if available) */
  labelUrl?: string

  /** ID of the parcel locker or service point (if used) */
  pickupLocationId?: string

  /** Name of the service point */
  pickupLocationName?: string

  /** Address of the pickup location */
  pickupAddress?: OrderAddressDTO

  /** Promised delivery date (ISO 8601 string) */
  estimatedDeliveryDate?: string

  /** When the package was shipped (ISO 8601 string) */
  shippedDate?: string

  /** Actual delivery timestamp if fulfilled (ISO 8601 string) */
  deliveryDate?: string

  /**
   * Raw pass through data from carrier if needed
   * @remarks Data should be an object with anything inside
   * @example
   * ```js
   *  something: "This is something",
   *  something2: {
   *    extra: "This can be an inside object",
   *  }
   * ```
   */
  additionalInfo?: OrderShippingAdditionalDataDTO
}

export type OrderDTO = DbLogFields & {
  id: string
  billingAddress?: OrderAddressDTO
  currency?: Currency
  customer: Option<CustomerDTO>
  discountCode?: string
  items?: OrderItemDTO[]
  orderAmount?: number
  orderDiscount?: number
  orderNumber?: Option<string>
  orderTaxAmount?: number
  paymentId?: string
  paymentProvider?: PaymentProviderName
  shippingAddress?: OrderAddressDTO
  shippingCost?: number
  shippingProvider?: ShippingProviderName
  shippingId?: string
  status: OrderStatus
  storeId: string
  shippingInfo?: OrderShippingDTO
}

export type CreateUpdateOrderDTO = Omit<OrderDTO, 'items' | 'customer' | 'id'> & {
  items: CreateOrderItemDTO[]
  customer?: Option<CreatCustomerDTO>
}

export interface OrderAddressDTO {
  address1: string
  address2: string
  attention: string
  country: string
  mobilePhone: string
  postalCode: string
  postalRegion: string
  reference: string
}

export interface CreateServiceItemDTO {
  quantity: number
  serviceId: string
  totalAmount: number
}

export interface OrderItemDTO {
  additionalInfo: string
  originalUnitPrice: number
  product: ProductLightDTO
  quantity: number
  services: ServiceItemDTO[] | null
  subItems: OrderItemDTO[] | null
  unitPrice: number
  vatRate: number
  name?: string
  brand?: string
  slug?: string
}

export interface ServiceItemDTO {
  service: ServiceDTO
  quantity: number
  totalAmount: number
}

export interface CreateOrderItemDTO {
  additionalInfo: string
  originalUnitPrice: number
  productId: string
  quantity: number
  services: CreateServiceItemDTO[] | null
  subItems: CreateOrderItemDTO[]
  unitPrice: number
  vatRate: number
  name?: string
  brand?: string
  slug?: string
}

export interface UpdateOrderStatusDTO {
  orderStatus: OrderStatus
}

export interface StorefrontOrderLineService {
  quantity: number
  service: ServiceDTO
  totalAmount: number
}

export interface StorefrontOrderLine<P extends ProductLightDTO = ProductLightDTO> {
  product: P
  quantity: number
  originalPrice: number
  unitPrice: number
  vatRate: number
  services: StorefrontOrderLineService[]
  subItems: CreateOrderItemDTO[]
}

export interface StorefrontDiscountCodeResponse {
  data: DiscountDTO
}

export interface StorefrontDiscountCodeListResponse {
  data: DiscountDTO[]
}

export interface UpdateOrderPayload {
  klarnaPayload?: Partial<KlarnaUpdateOrderRequest> & { orderId: string }
  nexiPayload?: NexiUpdateOrderRequest & { paymentId: string }
  sveaPayload?: SveaUpdateOrderModel & { orderId: string | number }
  // todo: add LEDYER
  ledyerPayload?: any
}

export interface CreateOrderClientPayload {
  locale: 'en' | 'sv'
  companyId: string
  items: CartItem[]
}

export interface CheckoutProduct {
  productId: string
  name: string
  brand: string
  quantity: number
  unitPrice: number
  slug: string
}

export interface CreateOrderRequest {
  storeId: string
  items: StorefrontOrderLine[]
  cartDiscountCode: Option<DiscountDTO>
  cartStateUuid: Option<string>
}

export interface CreateOrderResponse {
  klarnaResponse?: Option<KlarnaOrder>
  ingridResponse?: Option<IngridCreateSessionResponse>
  nexiResponse?: Option<NexiCreatePaymentResponse>
  sveaResponse?: Option<SveaOrder>
  storefrontOrderResponse: OrderDTO
}

export interface UpdateOrderResponse {
  klarnaResponse?: KlarnaOrder
  nexiResponse?: true
  sveaResponse?: SveaOrder
}

export interface GetOrderResponse {
  klarnaResponse?: Option<KlarnaOrder>
  nexiResponse?: Option<NexiRetrievePaymentResponse>
  sveaResponse?: Option<SveaOrder>
}
